import type { plans } from '@/lib/client';
import { REVERT_STATUS_TEXT, STEP_RUN_STATUS_TEXT } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { ChannelBadge } from './badges';

const STATUS_COLOR: Record<plans.StepRunStatus, string> = {
  pending: 'text-muted-foreground', running: 'text-blue-400', waiting_human: 'text-orange-400', completed: 'text-emerald-400', failed: 'text-red-400', skipped: 'text-muted-foreground',
};

const time = (iso?: string): string => (iso ? new Date(iso).toLocaleTimeString() : '');

/** The current run, step by step: what ran when, with what result. */
export function RunTimeline({ plan }: { plan: plans.PlanView }) {
  const run = plan.run;
  if (run === null) return null;
  const byKey = new Map(plan.steps.map((step) => [step.key, step]));
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-4 py-2 text-xs text-muted-foreground">
        <span>
          {run.trigger === 'scheduled' ? 'Scheduled run' : 'Run'} · {run.status.replace('_', ' ')} · started {time(run.started_at)}
          {run.finished_at && <> · finished {time(run.finished_at)}</>}
        </span>
        {run.failure && <span className="text-red-400">{run.failure.code}{run.failure.step_key ? ` at ${run.failure.step_key}` : ''}: {run.failure.message}</span>}
      </div>
      <ul className="divide-y divide-border/60">
        {run.steps.map((stepRun) => {
          const step = byKey.get(stepRun.step_key);
          return (
            <li key={stepRun.step_key} className="flex items-center gap-3 px-4 py-2 text-sm">
              <span className="w-24 shrink-0 font-mono text-[11px] text-muted-foreground">{time(stepRun.finished_at ?? stepRun.started_at)}</span>
              {step && <ChannelBadge channel={step.channel} />}
              <span className="min-w-0 flex-1 truncate text-foreground">{step?.title ?? stepRun.step_key}</span>
              <span className={cn('shrink-0 text-xs', STATUS_COLOR[stepRun.status])}>
                {STEP_RUN_STATUS_TEXT[stepRun.status]}
                {stepRun.revert_status && <> · {REVERT_STATUS_TEXT[stepRun.revert_status]}</>}
              </span>
              {stepRun.error && <span className="max-w-[40%] truncate text-xs text-red-400" title={stepRun.error.message}>{stepRun.error.message}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
